import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import WindowHeader from "../../Layout/WindowHeader/WindowHeader";
import SherComboBox from "../../ComboBoxes/SherComboBox";
import headeLogo from "../../../assets/images/SS.webp";
import { useDbContext } from "../../../context/DbContext";
import { useSelector } from "react-redux";
import { selectActiveFyid, selectActiveFy } from "../../../app/financialYearSlice";
import { selectActiveLocation } from "../../../app/locationSlice";
import { selectGeneralSettings } from "../../../app/settingsSlice";
import { useActiveFyLimits } from "../../../utils/fyDateValidation";

// ÔöÇÔöÇ Static Data ÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇ
const REPORT_TYPES = [
  { label: "Simple", value: "simpleReport" },
  { label: "Order by entry no", value: "reportOrderbyEntryno" },
  { label: "Cash/Bank Report", value: "cabaReport" },
  { label: "Closed Account Reports", value: "closedAccountsReport" },
  { label: "Closed Accounts", value: "closedAccounts" }
];

const parseDateToYyyyMmDd = (dateStr, fallback = "") => {
  if (!dateStr) return fallback;
  const cleanStr = String(dateStr).trim().split(/[\sT]+/)[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(cleanStr)) {
    return cleanStr;
  }
  let parts = cleanStr.split(/[-/]/);
  if (parts.length === 3) {
    let day = parseInt(parts[0], 10);
    let year = parseInt(parts[2], 10);
    if (year > 1000) {
      let monthPart = parts[1];
      let month = parseInt(monthPart, 10);
      if (isNaN(month)) {
        const monthsAbbrev = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
        month = monthsAbbrev.indexOf(monthPart.toLowerCase()) + 1;
      }
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      }
    }
  }
  const d = new Date(cleanStr);
  if (!isNaN(d.getTime())) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }
  return fallback;
};

function SchemeReportDia({ onClose, openReport, zIndex }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const fyid = useSelector(selectActiveFyid);
  const activeFy = useSelector(selectActiveFy);
  const activeLocation = useSelector(selectActiveLocation);
  const GeneralSettings = useSelector(selectGeneralSettings);

  // ÔöÇÔöÇ Session data ÔöÇÔöÇ
  const [userType, setUserType] = useState("");
  const [agentCode, setAgentCode] = useState("");
  const [userCode, setUserCode] = useState("");
  const [userAccno, setUserAccno] = useState("");
  const [cRate, setCRate] = useState("");

  // General settings for read-only controls
  const [genValues, setGenValues] = useState({
    readonlyAgentForEmployee: "",
    readonlyAccountForCustomer: "",
  });

  // ÔöÇÔöÇ Fetched options ÔöÇÔöÇ
  const [regCnameData, setRegCnameData] = useState([]);
  const [agents, setAgents] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [schemeForms, setSchemeForms] = useState([]);
  const [locations, setLocations] = useState([]);

  const getCurrentDate = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const today = getCurrentDate();

  const storeduType = sessionStorage.getItem("userType") || "";

  // ÔöÇÔöÇ Form State ÔöÇÔöÇ
  const [fromDate, setFromDate] = useState(() => (storeduType === "CUSTOMERS" ? "2010-01-01" : today));
  const [toDate, setToDate] = useState(today);
  const [reportType, setReportType] = useState("simpleReport");
  const [selectedForms, setSelectedForms] = useState([]);
  const [customer, setCustomer] = useState({ value: "", label: "" });
  const [agent, setAgent] = useState({ value: "", label: "" });
  const [accno, setAccNoState] = useState({ value: "", label: "" });
  const [mobileNo, setMobileNo] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(() => {
    const locId = Number(
      sessionStorage.getItem("loginLocationId") ||
      localStorage.getItem("loginLocationId") ||
      0
    );
    const locName =
      sessionStorage.getItem("loginLocation") ||
      localStorage.getItem("loginLocation") ||
      "";
    return locId ? { value: locId, label: locName || "SHOP" } : { value: 0, label: "All Locations" };
  });

  const { minDate, maxDate, isDateInFy } = useActiveFyLimits();

  const hasAlertedFyRef = useRef(false);
  // General Settings effect
  useEffect(() => {
    if (!Array.isArray(GeneralSettings)) return;
    const agentSetting = GeneralSettings.find(
      (item) => item.Name === "READ ONLY AGENT FIELD FOR EMPLOYEES IN SCHEME REPORT"
    );
    if (agentSetting) {
      setGenValues((prev) => ({ ...prev, readonlyAgentForEmployee: agentSetting.ss_status }));
    }
    const accSetting = GeneralSettings.find(
      (item) => item.Name === "READ ONLY ACCOUNT NO FIELD FOR CUSTOMERS IN SCHEME REPORT"
    );
    if (accSetting) {
      setGenValues((prev) => ({ ...prev, readonlyAccountForCustomer: accSetting.ss_status }));
    }
  }, [GeneralSettings]);

    // ÔöÇÔöÇ Load session details ÔöÇÔöÇ
  useEffect(() => {
    const storeduType = sessionStorage.getItem("userType") || "";
    const storedAgentCode = sessionStorage.getItem("agentCode") || "";
    const customerName = sessionStorage.getItem("CustomerName") || "";
    const accnoVal = sessionStorage.getItem("Uaccno") || "";

    setUserType(storeduType);
    setAgentCode(storedAgentCode);
    setUserCode(customerName);
    setUserAccno(accnoVal);

    if (activeFy && !hasAlertedFyRef.current) {
      if (!isDateInFy(today)) {
        alert("Current date is outside the active financial year");
        hasAlertedFyRef.current = true;
      }
    }
  }, [activeFy, today, isDateInFy]);

  // ÔöÇÔöÇ Fetch Locations ÔöÇÔöÇ
  useEffect(() => {
    if (!dbCode) return;
    axios
      .get(`${apiBaseUrl}/otherregistration/getoptions/${dbCode}`)
      .then((res) => {
        const all = res.data || [];
        const locs = all
          .filter((r) => String(r.Type || r.type || "").toLowerCase() === "location")
          .map((r) => ({
            label: r.Name || r.name || "",
            value: Number(r.Auto || r.auto) || 0
          }));
        setLocations([{ label: "All Locations", value: 0 }, ...locs]);

        // Pre-select current location by default
        const currentLocName = (
          activeLocation?.name ||
          sessionStorage.getItem("loginLocation") ||
          localStorage.getItem("loginLocation") ||
          ""
        ).trim().toLowerCase();

        const currentLocId = Number(
          activeLocation?.id ||
          sessionStorage.getItem("loginLocationId") ||
          localStorage.getItem("loginLocationId") ||
          0
        );

        const matchedLoc = locs.find(
          (l) => (currentLocId && l.value === currentLocId) ||
                 (currentLocName && l.label.trim().toLowerCase() === currentLocName)
        );

        if (matchedLoc) {
          setSelectedLocation(matchedLoc);
        } else if (locs.length > 0) {
          setSelectedLocation(locs[0]);
        }
      })
      .catch((err) => console.error("Error fetching locations:", err.message));
  }, [dbCode, apiBaseUrl, activeLocation]);

  // ÔöÇÔöÇ Fetch Gold Rate ÔöÇÔöÇ
  useEffect(() => {
    if (!dbCode) return;
    axios
      .get(`${apiBaseUrl}/main/getGoldRate/${dbCode}?date=${toDate || ""}`)
      .then((res) => {
        const rate = res.data[0]?.currentRate || "";
        setCRate(rate);
      })
      .catch((err) => console.error("Error fetching gold rate:", err.message));
  }, [dbCode, apiBaseUrl, toDate]);

  // ÔöÇÔöÇ Fetch Customers ÔöÇÔöÇ
  useEffect(() => {
    if (!dbCode) return;
    axios
      .get(`${apiBaseUrl}/main/SchRec_schemeregisteredcustomers/${dbCode}`)
      .then((res) => {
        setRegCnameData(res.data || []);
      })
      .catch((err) => console.error("Error fetching customers:", err.message));
  }, [dbCode, apiBaseUrl]);

  // ÔöÇÔöÇ Fetch Agents ÔöÇÔöÇ
  useEffect(() => {
    if (!dbCode) return;
    axios
      .get(`${apiBaseUrl}/main/schsalesmanNames/${dbCode}`)
      .then((res) => {
        setAgents(res.data || []);
      })
      .catch((err) => console.error("Error fetching agents:", err.message));
  }, [dbCode, apiBaseUrl]);

  // ÔöÇÔöÇ Fetch Account Numbers ÔöÇÔöÇ
  useEffect(() => {
    if (!dbCode) return;
    axios
      .get(`${apiBaseUrl}/main/SchRec_Accno/${dbCode}`)
      .then((res) => {
        setAccounts(res.data || []);
      })
      .catch((err) => console.error("Error fetching account numbers:", err.message));
  }, [dbCode, apiBaseUrl]);

  // ÔöÇÔöÇ Fetch Scheme Forms ÔöÇÔöÇ
  useEffect(() => {
    if (!dbCode) return;
    axios
      .get(`${apiBaseUrl}/common/selectAllformDatas/${dbCode}`)
      .then((res) => {
        const filtered = (res.data || [])
          .filter(
            (item) =>
              item.fmrTypeOfVoucher === "SCHEME RECEIPT" ||
              item.fmrTypeOfVoucher === "SCHEME REDEEM"
          )
          .map((item) => ({
            id: item.fmrEntryNo,
            label: item.fmrAbbreviation,
            voucherType: item.fmrTypeOfVoucher
          }));
        setSchemeForms(filtered);
        // By default, select all forms
        setSelectedForms(filtered.map((f) => f.label));
      })
      .catch((err) => console.error("Error fetching form registration data:", err.message));
  }, [dbCode, apiBaseUrl]);

  // ÔöÇÔöÇ Default settings based on UserType / Session values ÔöÇÔöÇ
  useEffect(() => {
    if (userType === "CUSTOMERS" && accounts.length > 0) {
      const match = accounts.find((item) => item.accno === userAccno);
      if (match) {
        setAccNoState({ value: match.accno, label: match.accno });
      }
    } else if (userType !== "CUSTOMERS" && agents.length > 0 && agentCode) {
      const match = agents.find((item) => item.Auto === parseInt(agentCode));
      if (match) {
        setAgent({ value: match.Auto, label: match.Name });
      }
    }
  }, [userType, accounts, agents, userAccno, agentCode]);

  // ÔöÇÔöÇ Derived combo options ÔöÇÔöÇ
  const customerOpts = regCnameData.map((c) => ({
    label: c.LedName,
    value: c.accname
  }));

  const agentOpts = agents.map((a) => ({
    label: a.Name,
    value: a.Auto
  }));

  const accNoOpts = useMemo(() => {
    const seen = new Set();
    const list = [];
    (accounts || []).forEach((ac) => {
      const numStr = String(ac.accno ?? "").trim();
      if (numStr && !seen.has(numStr)) {
        seen.add(numStr);
        list.push({ label: numStr, value: numStr });
      }
    });
    return list;
  }, [accounts]);

  // ÔöÇÔöÇ Handlers ÔöÇÔöÇ
  const handleToggleForm = (label) => {
    setSelectedForms((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    );
  };

  const handleSelectAllForms = () => {
    setSelectedForms(schemeForms.map((f) => f.label));
  };

  const handleUnSelectAllForms = () => setSelectedForms([]);

  const handleShowReport = () => {
    if (openReport) {
      const selectedFormIds = schemeForms
        .filter((f) => selectedForms.includes(f.label))
        .map((f) => f.id);

      // ÔöÇÔöÇ Reliable ID resolution ÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇÔöÇ
      // React stale closures mean customer.value / agent.value may hold the
      // display text instead of the numeric ID by the time Show is clicked.
      // The label (display name) is always correct ÔåÆ match against source data
      // to get the definitive numeric ID the SP requires.

      // customer ÔåÆ SP needs numeric Ledcode  (AND Ledcode = @customer, no quotes)
      const custMatch = regCnameData.find((c) => c.LedName === customer?.label);
      const customerFilterId = custMatch ? String(custMatch.accname) : "";

      // agent ÔåÆ SP needs numeric Auto ID  (WHERE fmrAgentId = @agent, no quotes)
      const agentMatch = agents.find((a) => a.Name === agent?.label);
      const agentFilterId = agentMatch ? String(agentMatch.Auto) : "";

      // accno ÔåÆ SP needs the accno string  (AND a.accno = @Ano, with quotes)
      const accnoFilterStr = accno?.label || accno?.value || "";

      if (userType !== "CUSTOMERS" && fromDate && !isDateInFy(fromDate)) {
        alert("From Date incompatible with financial year");
        return;
      }
      if (toDate && !isDateInFy(toDate)) {
        alert("To Date incompatible with financial year");
        return;
      }

      openReport({
        reportData: {
          fromDate: userType === "CUSTOMERS" ? "2010-01-01" : fromDate,
          toDate,
          reportType,
          customer: customerFilterId,  // numeric Ledcode  e.g. "42"
          customerName: customer?.label || "",
          agent:    agentFilterId,     // numeric Auto ID  e.g. "5"
          agentName: agent?.label || "",
          accno:    accnoFilterStr,    // accno string     e.g. "GS001"
          mobileNo: userType === "CUSTOMERS" ? "" : mobileNo,
          selectedFormsText: selectedForms.join(", "),
          fyid: fyid || activeFy?.fyid || 0,
          locationId: selectedLocation?.value ? Number(selectedLocation.value) : 0,
          locationName: selectedLocation?.label || "All Locations"
        },
        forms: selectedFormIds
      });
    }
  };

  const cbFont = { fontFamily: "Inter, sans-serif" };
  const labelCls = "text-[11px] text-black font-Inter flex-shrink-0";

  return (
    <div
      className="fixed inset-0 w-full h-full flex items-center justify-center bg-black bg-opacity-40"
      style={{ zIndex: zIndex || 100 }}
    >
      <div
        className="bg-white rounded-[10px] shadow-2xl flex flex-col overflow-hidden font-Inter"
        style={{ width: "680px", maxWidth: "98vw", height: "430px", maxHeight: "90vh" }}
      >
        {/* Header */}
        <WindowHeader
          title="Scheme Report"
          logo={headeLogo}
          onClose={onClose}
          className="bg-[#0f56d3] h-[40px] text-white font-Inter"
          closeBtnClassName="w-6 h-6 rounded-[5px] border-none mr-2 flex items-center justify-center"
        />

        {/* Body */}
        <div className="flex flex-1 overflow-hidden min-h-0">
          
          {/* Left panel: Forms list */}
          <div className="flex flex-col border-r border-gray-300" style={{ width: "190px", minWidth: "190px" }}>
            <div className="px-3 py-[6px] text-[11px] font-semibold text-black font-Inter bg-[#eef1f8] border-b border-gray-300">
              Select Scheme Form
            </div>
            <div className="flex-1 overflow-y-auto bg-white">
              {schemeForms.map((formObj) => (
                <label
                  key={formObj.id}
                  className={`flex items-center gap-2 px-2 py-[5px] cursor-pointer select-none text-[11px] font-Inter font-medium border-b border-gray-100 last:border-b-0 ${
                    selectedForms.includes(formObj.label)
                      ? "bg-[#dbeafe] text-[#1e40af]"
                      : "bg-white text-black hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedForms.includes(formObj.label)}
                    onChange={() => handleToggleForm(formObj.label)}
                    className="w-3.5 h-3.5 rounded border-gray-400 accent-[#1a56db] flex-shrink-0 cursor-pointer"
                  />
                  <span className="truncate">{formObj.label}</span>
                </label>
              ))}
            </div>
            <div className="flex gap-1 p-2 border-t border-gray-200 bg-[#f9fafb]">
              <button type="button" onClick={handleSelectAllForms} className="flex-1 py-[3px] px-1 border border-gray-300 rounded-[3px] bg-white hover:bg-gray-100 text-[10px] text-black font-semibold cursor-pointer font-Inter">
                Select All
              </button>
              <button type="button" onClick={handleUnSelectAllForms} className="flex-1 py-[3px] px-1 border border-gray-300 rounded-[3px] bg-white hover:bg-gray-100 text-[10px] text-black font-semibold cursor-pointer font-Inter">
                UnSelect All
              </button>
            </div>
          </div>

          {/* Right panel: Filter inputs */}
          <div className="flex flex-col flex-1 overflow-hidden">
            
            {/* Scrollable Filters */}
            <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-3 font-Inter">
              
              {/* Dates row */}
              <div className="flex items-center gap-2">
                <span className={`${labelCls} w-[90px]`}>From Date</span>
                <input
                  type="date"
                  min={minDate}
                  max={maxDate}
                  value={userType === "CUSTOMERS" ? "2010-01-01" : fromDate}
                  onChange={(e) => {
                    if (userType !== "CUSTOMERS") {
                      setFromDate(e.target.value);
                    }
                  }}
                  onBlur={(e) => {
                    if (userType !== "CUSTOMERS") {
                      const val = e.target.value;
                      if (val && !isDateInFy(val)) {
                        alert("Date incompatible with financial year");
                      }
                    }
                  }}
                  disabled={userType === "CUSTOMERS"}
                  className="flex-1 border border-gray-300 rounded-[3px] h-6 px-2 text-[11px] text-black font-Inter focus:outline-none focus:ring-1 focus:ring-[#0f56d3] disabled:bg-gray-100"
                />
                <span className={`${labelCls} w-[60px] text-right`}>To Date</span>
                <input
                  type="date"
                  min={minDate}
                  max={maxDate}
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val && !isDateInFy(val)) {
                      alert("Date incompatible with financial year");
                    }
                  }}
                  className="flex-1 border border-gray-300 rounded-[3px] h-6 px-2 text-[11px] text-black font-Inter focus:outline-none focus:ring-1 focus:ring-[#0f56d3]"
                />
              </div>

              {/* Rate row for CUSTOMERS */}
              {userType === "CUSTOMERS" && (
                <div className="flex items-center gap-2">
                  <span className={`${labelCls} w-[90px]`}>Rate</span>
                  <input
                    type="text"
                    readOnly
                    value={cRate}
                    className="flex-1 border border-gray-300 rounded-[3px] h-6 px-2 text-[11px] text-black font-Inter bg-gray-100"
                  />
                </div>
              )}

              {/* Report Type */}
              <div className="flex items-center gap-2">
                <span className={`${labelCls} w-[90px]`}>Report Type</span>
                <div className="flex-1">
                  <SherComboBox
                    options={REPORT_TYPES}
                    value={reportType}
                    onSelect={(opt) => setReportType(opt.value)}
                    inputClassName="w-full border border-gray-300 rounded-[3px] font-Inter"
                    inputStyle={cbFont}
                    inputHeight="24px"
                  />
                </div>
              </div>

              {/* Location Dropdown */}
              <div className="flex items-center gap-2">
                <span className={`${labelCls} w-[90px]`}>Location</span>
                <div className="flex-1">
                  <SherComboBox
                    options={locations}
                    value={selectedLocation?.label || "All Locations"}
                    onSelect={(opt) => setSelectedLocation(opt || { value: 0, label: "All Locations" })}
                    onInputChange={(text) => {
                      if (!text || text === "" || text.toLowerCase() === "all" || text.toLowerCase() === "all locations") {
                        setSelectedLocation({ value: 0, label: "All Locations" });
                      } else {
                        const match = locations.find((l) => l.label.toLowerCase() === text.toLowerCase());
                        setSelectedLocation(match || { value: 0, label: text });
                      }
                    }}
                    inputClassName="w-full border border-gray-300 rounded-[3px] font-Inter"
                    inputStyle={cbFont}
                    inputHeight="24px"
                  />
                </div>
              </div>

              {/* Customer Dropdown */}
              <div className="flex items-center gap-2">
                <span className={`${labelCls} w-[90px]`}>Scheme Customer</span>
                <div className="flex-1">
                  <SherComboBox
                    options={customerOpts}
                    value={customer?.label || ""}
                    onSelect={(opt) => setCustomer(opt)}
                    onInputChange={(text) => {
                      if (text === "") {
                        setCustomer({ value: "", label: "" });
                      } else if (text !== customer?.label) {
                        // User typing new search ÔÇö value becomes text until they pick
                        setCustomer({ value: text, label: text });
                      }
                      // text === label means combo auto-filled after onSelect ÔÇö keep numeric ID
                    }}
                    readOnly={userType !== "ADMIN"}
                    inputClassName="w-full border border-gray-300 rounded-[3px] font-Inter disabled:bg-gray-100"
                    inputStyle={cbFont}
                    inputHeight="24px"
                  />
                </div>
              </div>

              {/* Agent Dropdown */}
              {userType !== "CUSTOMERS" && (
                <div className="flex items-center gap-2">
                  <span className={`${labelCls} w-[90px]`}>Scheme Agent</span>
                  <div className="flex-1">
                    <SherComboBox
                      options={agentOpts}
                      value={agent?.label || ""}
                      onSelect={(opt) => setAgent(opt)}
                      onInputChange={(text) => {
                        if (text === "") {
                          setAgent({ value: "", label: "" });
                        } else if (text !== agent?.label) {
                          // User typing new search ÔÇö value becomes text until they pick
                          setAgent({ value: text, label: text });
                        }
                        // text === label means combo auto-filled after onSelect ÔÇö keep numeric ID
                      }}
                      readOnly={userType === "EMPLOYEE" && genValues.readonlyAgentForEmployee === 1}
                      inputClassName="w-full border border-gray-300 rounded-[3px] font-Inter disabled:bg-gray-100"
                      inputStyle={cbFont}
                      inputHeight="24px"
                    />
                  </div>
                </div>
              )}

              {/* Account Number Dropdown */}
              <div className="flex items-center gap-2">
                <span className={`${labelCls} w-[90px]`}>Account Number</span>
                <div className="flex-1">
                  <SherComboBox
                    options={accNoOpts}
                    value={accno?.label || ""}
                    onSelect={(opt) => setAccNoState(opt || { value: "", label: "" })}
                    onInputChange={(text) => {
                      setAccNoState({ value: text, label: text });
                    }}
                    onFreeInput={(text) => {
                      setAccNoState({ value: text, label: text });
                    }}
                    onClear={() => {
                      setAccNoState({ value: "", label: "" });
                    }}
                    readOnly={userType === "CUSTOMERS" && genValues.readonlyAccountForCustomer === 1}
                    inputClassName="w-full border border-gray-300 rounded-[3px] font-Inter disabled:bg-gray-100"
                    inputStyle={cbFont}
                    inputHeight="24px"
                  />
                </div>
              </div>

              {/* Phone Number Input */}
              {userType !== "CUSTOMERS" && (
                <div className="flex items-center gap-2">
                  <span className={`${labelCls} w-[90px]`}>Phone Number</span>
                  <input
                    type="text"
                    value={mobileNo}
                    onChange={(e) => setMobileNo(e.target.value)}
                    className="flex-1 border border-gray-300 rounded-[3px] h-6 px-2 text-[11px] text-black font-Inter focus:outline-none focus:ring-1 focus:ring-[#0f56d3]"
                  />
                </div>
              )}

            </div>

            {/* Footer Buttons fixed at the bottom of the panel */}
            <div className="flex justify-center items-center gap-4 border-t border-gray-200 py-2.5 bg-[#f8faff] shrink-0">
              <button
                type="button"
                onClick={handleShowReport}
                className="bg-[#2563eb] text-white hover:bg-blue-700 transition flex items-center justify-center gap-2 py-1.5 px-8 rounded-[4px] shadow-sm font-semibold cursor-pointer text-xs font-Inter border-none"
              >
                Show
              </button>
              <button
                type="button"
                onClick={onClose}
                className="bg-white text-black hover:bg-gray-50 border border-gray-300 transition flex items-center justify-center gap-2 py-1.5 px-8 rounded-[4px] shadow-sm font-semibold cursor-pointer text-xs font-Inter"
              >
                Exit
              </button>
            </div>

          </div>

        </div>
      </div>
    </div>
  );
}

export default SchemeReportDia;
